// js/Views/Support/support.js
/* =============================================================================
   DONATION SUPPORT PAGE - MAIN MODULE
   Path: /js/views/donation/support.js
   
   Purpose: Donation form handling, payment processing, validation, UI state
   Dependencies: 
   - /js/_shared/_utils.js (load first)
   - /js/_shared/_toast.js
   - /js/_shared/_theme.js
   
   Namespace: window.DONATION.Support
   ========================================================================== */

window.DONATION = window.DONATION || {};

window.DONATION.Support = (function () {
    'use strict';

    /* =========================================================================
       PRIVATE VARIABLES
       ========================================================================= */

    /**
     * @type {{
     *   eventId: number|null,
     *   advocateId: number|null,
     *   contactId: number|null,
     *   minimumDonation: number,
     *   feePercentage: number,
     *   submitUrl: string,
     *   sourceChannel: string,
     *   trackingCode: string,
     *   acceptJsClientKey: string,
     *   acceptJsApiLoginId: string
     * }}
     * Module configuration populated from server-provided options during init.
     */
    var _config = {
        eventId: null,
        advocateId: null,
        contactId: null,
        minimumDonation: 10,
        feePercentage: 0.035,
        submitUrl: '/Donation/Submit',
        sourceChannel: 'direct',
        rawSourceChannel: '',
        trackingCode: '',
        // Authorize.Net Accept.js credentials (set from server)
        acceptJsClientKey: '',
        acceptJsApiLoginId: '',
        // Google Pay merchant ID (from Google Pay Business Console)
        googlePayMerchantId: '',
        // Authorize.Net Payment Gateway ID for Google Pay tokenization
        googlePayGatewayMerchantId: '',
        // PayPal client ID (from PayPal Developer portal)
        paypalClientId: '',
        // Sandbox mode flag
        useSandbox: true,
        // Digital wallet toggles (from Company settings)
        enableApplePay: false,
        enableGooglePay: false,
        enablePayPal: false,
        enableVenmo: false,
        // Context for PayPal line items (set from server)
        eventName: '',
        advocateName: '',
        organizationName: '',
        gaMeasurementId: ''
    };

    /**
     * @type {{
     *   selectedAmount: number|null,
     *   isProcessing: boolean,
     *   paymentToken: {dataDescriptor: string, dataValue: string}|null
     * }}
     * Mutable runtime state for the donation form.
     */
    var _state = {
        selectedAmount: null,
        isProcessing: false,
        paymentToken: null,
        paymentMethod: 'CARD',
        /** @type {{ cardNetwork: string, lastFour: string }|null} */
        paymentMethodDetail: null
    };

    /** Reusable Google Pay PaymentsClient (created once in _detectGooglePay). */
    var _googlePayClient = null;

    /**
     * @type {Object.<string, HTMLElement|NodeList|null>}
     * Cached DOM element references, populated by _cacheElements().
     */
    var _elements = {};

    /**
     * Localized UI strings — populated from window.i18n (server .resx) at init.
     * English defaults ensure the page works even if i18n partial is missing.
     */
    var _i18n = {
        // Validation
        firstNameRequired: 'First name is required',
        lastNameRequired: 'Last name is required',
        invalidEmail: 'Please enter a valid email address',
        invalidCard: 'Please enter a valid card number',
        invalidMonth: 'Invalid month',
        invalidZip: 'Please enter billing ZIP code',
        invalidExpiry: 'Please enter expiry as MM/YY',
        invalidCVV: 'Please enter CVV',
        minimumDonation: 'Minimum donation is $',
        selectDonationAmount: 'Please select or enter a donation amount (minimum $',
        // Payment
        processing: 'Processing...',
        donateNow: 'Donate Now',
        paymentFailed: 'Payment failed',
        networkError: 'Network error',
        captureFailed: 'Capture failed',
        couldNotCreatePayment: 'Could not create payment.',
        couldNotCreatePaymentRetry: 'Could not create payment. Please try again.',
        unsupportedPaymentMethod: 'Unsupported payment method.',
        paymentSystemLoading: 'Payment system is loading. Please try again in a moment.',
        paymentValidationFailed: 'Payment validation failed. Please check your card details.',
        errorTryAgain: 'An error occurred. Please try again.',
        tooManyAttempts: 'Too many payment attempts. Please wait {0} seconds and try again.',
        walletPaymentFailed: '{0} payment failed. Please try card payment.',
        applePayUnavailable: 'Apple Pay is temporarily unavailable. Please use card payment.',
        googlePayNotAvailable: 'Google Pay is not available. Please try card payment.',
        // Payment methods
        creditCard: 'Credit Card',
        // Processing overlay
        processingYourDonation: 'Processing your donation...',
        // Success modal
        linkCopied: 'Link copied!',
        moreSharing: 'More sharing options'
    };

    /* =========================================================================
       GA4 ANALYTICS
       ========================================================================= */

    /**
     * Sends a GA4 event if gtag is loaded and a measurement ID is configured.
     * @param {string} eventName - GA4 event name (e.g. 'purchase', 'begin_checkout')
     * @param {Object} [params] - Event parameters
     */
    function _trackEvent(eventName, params) {
        if (typeof gtag !== 'function' || !_config.gaMeasurementId) return;
        var base = {
            event_name: _config.eventName,
            advocate_name: _config.advocateName,
            organization_name: _config.organizationName,
            source_channel: _config.sourceChannel
        };
        gtag('event', eventName, Object.assign(base, params || {}));
    }

    /* =========================================================================
       AUTO-TRANSLATE
       ========================================================================= */

    /**
     * Translates the event description to the current language if not English.
     * Uses the free MyMemory translation API.
     */
    function _translateEventDescription() {
        var lang = (window.i18n && window.i18n.lang) || 'en';
        if (lang === 'en') return;

        var el = document.getElementById('eventDescription');
        if (!el || !el.textContent.trim()) return;

        var text = el.textContent.trim();
        var url = 'https://api.mymemory.translated.net/get?q=' +
            encodeURIComponent(text) + '&langpair=en|' + lang;

        fetch(url)
            .then(function (r) { return r.json(); })
            .then(function (data) {
                if (data.responseStatus === 200 && data.responseData && data.responseData.translatedText) {
                    el.textContent = data.responseData.translatedText;
                }
            })
            .catch(function () { /* keep original text on failure */ });
    }

    /* =========================================================================
       INITIALIZATION
       ========================================================================= */

    /**
     * Initializes the module with server-provided config options.
     * Caches DOM elements, binds events, applies theme, and sets up UI animations.
     * @param {Object} [options] - Server-provided configuration values to merge into _config.
     * @param {number} [options.eventId] - The event ID for this donation page.
     * @param {number} [options.advocateId] - The advocate ID associated with this donation.
     * @param {number} [options.contactId] - The pre-identified contact ID, if known.
     * @param {number} [options.minimumDonation] - Minimum allowed donation amount in dollars.
     * @param {number} [options.feePercentage] - Processing fee percentage as a decimal (e.g. 0.035 for 3.5%).
     * @param {string} [options.submitUrl] - Server endpoint URL for donation submission.
     * @param {string} [options.sourceChannel] - Traffic source channel identifier.
     * @param {string} [options.trackingCode] - Campaign tracking code.
     * @param {string} [options.acceptJsClientKey] - Authorize.Net Accept.js client key.
     * @param {string} [options.acceptJsApiLoginId] - Authorize.Net Accept.js API login ID.
     * @returns {void}
     */
    function init(options) {

        if (options) {
            Object.keys(options).forEach(function (key) {
                if (_config.hasOwnProperty(key)) {
                    _config[key] = options[key];
                }
            });
        }

        // Merge i18n strings from server (window.i18n.strings)
        if (window.i18n && window.i18n.strings) {
            Object.keys(window.i18n.strings).forEach(function (key) {
                if (_i18n.hasOwnProperty(key)) {
                    _i18n[key] = window.i18n.strings[key];
                }
            });
        }

        _translateEventDescription();
        _cacheElements();
        _bindEvents();
        _initializeTheme();
        _updateFeeCalculation();
        _showFeeBreakdownIfChecked();
        _animateProgressBar();
        _initializeFadeIn();
        _detectWallets();

        // GA4: Set user role for segmentation
        if (typeof gtag === 'function' && _config.gaMeasurementId) {
            gtag('set', 'user_properties', { user_role: 'donor' });
        }

        // GA4: Track donation page view with context (items enables ecommerce funnel)
        _trackEvent('view_item', {
            currency: 'USD',
            value: _config.minimumDonation,
            items: [{
                item_id: 'donation_' + _config.eventId,
                item_name: _config.eventName || 'Donation',
                item_category: 'Donation',
                price: _config.minimumDonation,
                quantity: 1
            }]
        });

    }

    /**
     * Caches all DOM element references into the _elements object.
     * Includes form inputs, buttons, error displays, fee breakdown elements,
     * success modal elements, and the progress bar.
     * @returns {void}
     */
    function _cacheElements() {
        _elements = {
            wrapper: qs('.donation-wrapper'),
            form: qs('#donationForm'),
            amountButtons: qsa('.amount-btn'),
            amountInput: qs('#donationAmount'),
            submitButton: qs('#btnDonate'),
            successModal: qs('#successModal'),
            firstName: qs('#firstName'),
            lastName: qs('#lastName'),
            email: qs('#email'),
            phone: qs('#phone'),
            message: qs('#message'),
            cardNumber: qs('#cardNumber'),
            cardExpiry: qs('#cardExpiry'),
            cardCVV: qs('#cardCVV'),
            billingZip: qs('#billingZip'),
            coverFees: qs('#coverFees'),
            feeBreakdown: qs('#feeBreakdown'),
            feeDescription: qs('#feeDescription'),
            feeDisplayDonation: qs('#feeDisplayDonation'),
            feeDisplayFee: qs('#feeDisplayFee'),
            feeDisplayTotal: qs('#feeDisplayTotal'),
            amountError: qs('#amountError'),
            firstNameError: qs('#firstNameError'),
            lastNameError: qs('#lastNameError'),
            emailError: qs('#emailError'),
            paymentError: qs('#paymentError'),
            successAmount: qs('#successAmount'),
            successPaymentMethod: qs('#successPaymentMethod'),
            successProcessingFee: qs('#successProcessingFee'),
            successTotalCharged: qs('#successTotalCharged'),
            successTransaction: qs('#successTransaction'),
            processingFeeRow: qs('#processingFeeRow'),
            progressFill: qs('.progress-fill'),
            headerDonateBtn: qs('#headerDonateBtn'),
            // Payment method tabs & wallet UI
            paymentMethodTabs: qs('#paymentMethodTabs'),
            cardFieldsContainer: qs('#cardFieldsContainer'),
            applePayTab: qs('#applePayTab'),
            applePayButtonContainer: qs('#applePayButtonContainer'),
            googlePayTab: qs('#googlePayTab'),
            googlePayButtonContainer: qs('#googlePayButtonContainer'),
            paypalTab: qs('#paypalTab'),
            venmoTab: qs('#venmoTab'),
            paypalButtonContainer: qs('#paypalButtonContainer'),
            venmoButtonContainer: qs('#venmoButtonContainer'),
            btnDonate: qs('#btnDonate')
        };
    }

    /**
     * Wires up all event listeners for the donation form.
     * Binds amount button clicks, custom amount input, cover-fees checkbox,
     * form submit, card input formatting, and header donate button scroll.
     * @returns {void}
     */
    function _bindEvents() {
        _elements.amountButtons.forEach(function (btn) {
            btn.addEventListener('click', _handleAmountClick);
        });

        if (_elements.amountInput) {
            _elements.amountInput.addEventListener('input', _handleAmountInput);
            _elements.amountInput.addEventListener('change', _handleAmountInput);
        }

        if (_elements.coverFees) {
            _elements.coverFees.addEventListener('change', _updateFeeCalculation);
        }

        if (_elements.form) {
            _elements.form.addEventListener('submit', _handleSubmit);
        }

        if (_elements.cardNumber) {
            _elements.cardNumber.addEventListener('input', _formatCardNumber);
        }
        if (_elements.cardExpiry) {
            _elements.cardExpiry.addEventListener('input', _formatCardExpiry);
        }
        if (_elements.cardCVV) {
            _elements.cardCVV.addEventListener('input', _formatCardCVV);
        }
        if (_elements.billingZip) {
            _elements.billingZip.addEventListener('input', _formatBillingZip);
        }

        if (_elements.headerDonateBtn) {
            _elements.headerDonateBtn.addEventListener('click', _scrollToForm);
        }

        // Payment method tab switching
        if (_elements.paymentMethodTabs) {
            _elements.paymentMethodTabs.querySelectorAll('.payment-tab').forEach(function (tab) {
                tab.addEventListener('click', function () { _selectPaymentMethod(tab.dataset.method); });
            });
        }

    }

    /* =========================================================================
       PAYMENT METHOD SELECTION & WALLET DETECTION
       ========================================================================= */

    /**
     * Detects available digital wallets and shows their tabs.
     */
    function _detectWallets() {
        // Apple Pay: available in Safari with a configured wallet
        if (_config.enableApplePay && window.ApplePaySession && ApplePaySession.canMakePayments()) {
            if (_elements.applePayTab) _elements.applePayTab.style.display = '';
            _renderApplePayButton();
        }

        // Google Pay: check via Google Pay API (SDK loads async, so retry if not ready)
        if (_config.enableGooglePay) _detectGooglePay(0);

        // PayPal/Venmo: load SDK dynamically if client ID is configured
        if (_config.enablePayPal || _config.enableVenmo) _loadPayPalSdk();
    }

    /**
     * Renders the official Apple Pay SDK donate button into the container.
     */
    function _renderApplePayButton() {
        var container = document.getElementById('applePayButtonContainer');
        if (!container) return;

        var btn = document.createElement('apple-pay-button');
        btn.setAttribute('buttonstyle', 'black');
        btn.setAttribute('type', 'donate');
        btn.setAttribute('locale', 'en-US');
        btn.addEventListener('click', _handleWalletPay);
        container.appendChild(btn);
    }

    function _detectGooglePay(attempt) {
        if (window.google && google.payments && google.payments.api) {
            var gpEnv = _config.useSandbox ? 'TEST' : 'PRODUCTION';
            var clientConfig = { environment: gpEnv };
            if (!_config.useSandbox && _config.googlePayMerchantId) {
                clientConfig.merchantInfo = { merchantId: _config.googlePayMerchantId };
            }
            _googlePayClient = new google.payments.api.PaymentsClient(clientConfig);
            _googlePayClient.isReadyToPay({
                apiVersion: 2,
                apiVersionMinor: 0,
                allowedPaymentMethods: [{
                    type: 'CARD',
                    parameters: { allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'], allowedCardNetworks: ['VISA', 'MASTERCARD', 'AMEX', 'DISCOVER'] }
                }]
            }).then(function (response) {
                if (response.result) {
                    if (_elements.googlePayTab) _elements.googlePayTab.style.display = '';
                    _renderGooglePayButton();
                }
            }).catch(function () { /* Google Pay not available */ });
        } else if (attempt < 10) {
            setTimeout(function () { _detectGooglePay(attempt + 1); }, 500);
        }
    }

    /**
     * Renders the official Google Pay button via the SDK createButton API.
     */
    function _renderGooglePayButton() {
        var container = document.getElementById('googlePayButtonContainer');
        if (!container || !_googlePayClient) return;

        var button = _googlePayClient.createButton({
            onClick: _handleWalletPay,
            buttonType: 'donate',
            buttonColor: 'default',
            buttonSizeMode: 'fill',
            buttonRadius: 8
        });

        container.appendChild(button);
    }

    /**
     * Dynamically loads the PayPal JavaScript SDK and renders PayPal/Venmo buttons.
     */
    function _loadPayPalSdk() {
        if (!_config.paypalClientId) return;

        var script = document.createElement('script');
        script.src = 'https://www.paypal.com/sdk/js?client-id=' +
            encodeURIComponent(_config.paypalClientId) +
            '&enable-funding=venmo&currency=USD&intent=capture';
        script.onload = function () {
            _initPayPalButtons();

            // AB#461: eligibility is decided here, not only when the tab is clicked. Checking at
            // click time would show the option, then make it disappear under the donor's finger.
            // A wallet the SDK will not render in this browser is simply never offered.
            if (_config.enablePayPal && _elements.paypalTab && _walletEligible(paypal.FUNDING.PAYPAL)) {
                _elements.paypalTab.style.display = '';
            }
            if (_config.enableVenmo && _elements.venmoTab && _walletEligible(paypal.FUNDING.VENMO)) {
                _elements.venmoTab.style.display = '';
            }
        };
        script.onerror = function () {
            console.warn('[DONATION.Support] PayPal SDK failed to load');
        };
        document.body.appendChild(script);
    }

    /**
     * Stores PayPal SDK readiness (set true once SDK loads and _initPayPalButtons runs).
     * Actual button rendering is deferred until the user clicks the PayPal/Venmo tab
     * so the container is visible and the SDK iframe gets proper dimensions.
     */
    function _initPayPalButtons() {
        // SDK is loaded — tabs are already shown. Buttons will be lazy-rendered
        // in _selectPaymentMethod() when the user first activates each tab.
    }

    /**
     * Validates amount + form fields before PayPal/Venmo SDK opens the popup.
     * Called from the SDK onClick callback — returns true if valid, false to reject.
     * @returns {boolean}
     */
    function _validateBeforeWalletPay() {
        var amount = _getCurrentAmount();
        if (!amount || amount < _config.minimumDonation) {
            _showError('amountError', _i18n.selectDonationAmount + _config.minimumDonation + ').');
            _scrollToError(_elements.amountInput || qs('#donationAmount'));
            return false;
        }
        if (!_validateForm()) return false;
        return true;
    }

    /**
     * AB#425: turns a throttled response into an error carrying a message the donor can act on.
     *
     * Every donation endpoint (card submit, PayPal create and capture, pledge) shares ONE per-IP
     * rate-limit bucket, so a 429 is a state a real donor can reach - two people donating from the
     * same office or school connection, or one person retrying a declined card. The server answers
     * with RFC 9110 Retry-After; nothing here read it, so the page said "an error occurred" when
     * the truth was "wait forty seconds". On a payment form that reads as a failed donation and the
     * donor leaves.
     *
     * The message rides on `userMessage` because the catch handlers deliberately show a fixed
     * string rather than raw error text - a gateway exception must not reach the donor. This is an
     * explicit opt-in for the one case where the server's meaning IS the right thing to show.
     *
     * Returns null when the response is fine, so callers read as a guard.
     */
    function _throttleError(response) {
        if (!response) return null;

        // Detect on the HEADER, not the status code. Only Development sees a clean 429: every other
        // environment runs UseStatusCodePagesWithReExecute("/Error/{0}"), which re-executes the
        // rejected request through the error page and rewrites the status - prod answers 400 - while
        // preserving the headers already written. Keying on 429 alone worked on dev and silently did
        // nothing in prod, which is the only place it matters.
        //
        // Retry-After is the reliable signal because the rate limiter's OnRejected is the only thing
        // in this app that sets it.
        var retryAfter = response.headers.get('Retry-After');
        if (response.status !== 429 && !retryAfter) return null;

        // Retry-After may be absent or junk if something between us and the client rewrites it.
        // A minute matches the limiter's fixed window and is never misleadingly short.
        var seconds = parseInt(retryAfter, 10);
        if (!isFinite(seconds) || seconds < 1) seconds = 60;

        var message = _i18n.tooManyAttempts.replace('{0}', seconds);
        var error = new Error(message);
        error.userMessage = message;
        return error;
    }

    /**
     * Shared server-side createOrder for PayPal/Venmo.
     * Validation already passed in onClick, so we go straight to order creation.
     */
    function _paypalCreateOrder() {
        var amount = _getCurrentAmount();
        var coverFees = _elements.coverFees ? _elements.coverFees.checked : false;
        var fee = coverFees ? _calculateFee(amount) : 0;

        var description = 'Donation';
        if (_config.eventName) {
            description = _config.advocateName
                ? 'Donation via ' + _config.advocateName + ' for ' + _config.eventName
                : 'Donation to ' + _config.eventName;
        }

        return fetch('/Support/CreatePayPalOrder', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'RequestVerificationToken': AppUtils.getToken()
            },
            body: JSON.stringify({
                amount: amount,
                fee: fee,
                description: description,
                eventId: _config.eventId ? parseInt(_config.eventId, 10) : null,
                advocateId: _config.advocateId ? parseInt(_config.advocateId, 10) : null,
                contactId: _config.contactId ? parseInt(_config.contactId, 10) : null
            })
        })
        .then(function (res) {
            // AB#425: a 429 has no JSON body, so res.json() rejected with a parse error before
            // anything could explain it. Check the status first.
            var throttled = _throttleError(res);
            if (throttled) throw throttled;
            if (!res.ok) throw new Error('HTTP error ' + res.status);
            return res.json();
        })
        .then(function (result) {
            if (result.orderId) {
                return result.orderId;
            } else {
                console.error('[DONATION.Support] createOrder failed:', result);
                _showError('paymentError', result.error || _i18n.couldNotCreatePayment);
            }
        })
        .catch(function (err) {
            console.error('[DONATION.Support] createOrder error:', err);
            _showError('paymentError', err.userMessage || _i18n.couldNotCreatePaymentRetry);
        });
    }

    /**
     * Shared server-side onApprove (capture) for PayPal/Venmo.
     */
    function _paypalOnApprove(data, actions, paymentMethod) {
        _state.isProcessing = true;
        _setButtonLoading(true);

        // Build capture request with full form data so the server can save
        // the donation atomically with the PayPal capture — prevents orphaned
        // payments if the browser closes before _submitDonation fires.
        var amount = _getCurrentAmount();
        var coverFees = _elements.coverFees ? _elements.coverFees.checked : false;
        var processingFee = coverFees ? _calculateFee(amount) : 0;

        var capturePayload = {
            orderId: data.orderID,
            eventId: parseInt(_config.eventId, 10),
            advocateId: parseInt(_config.advocateId, 10),
            contactId: _config.contactId ? parseInt(_config.contactId, 10) : null,
            donationAmount: amount,
            totalAmount: amount + processingFee,
            coverFees: coverFees,
            processingFee: processingFee,
            firstName: _elements.firstName.value.trim(),
            lastName: _elements.lastName.value.trim(),
            email: _elements.email.value.trim(),
            phone: _elements.phone ? _elements.phone.value.trim() : null,
            message: _elements.message ? _elements.message.value.trim() : null,
            paymentMethod: paymentMethod,
            sourceChannel: _config.sourceChannel || 'direct',
            rawSourceChannel: _config.rawSourceChannel || null,
            trackingCode: _config.trackingCode || null,
            billingZip: _elements.billingZip ? _elements.billingZip.value.trim() : null,
            usedRoundUpNudge: qs('#usedRoundUpNudge') ? qs('#usedRoundUpNudge').value === 'true' : false,
            originalAmountBeforeNudge: qs('#originalAmountBeforeNudge') ? parseFloat(qs('#originalAmountBeforeNudge').value) || 0 : 0
        };

        return fetch('/Support/CapturePayPalOrder', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'RequestVerificationToken': AppUtils.getToken()
            },
            body: JSON.stringify(capturePayload)
        })
        .then(function (res) {
            // AB#425: same guard as createOrder. Capture shares the donation rate-limit bucket,
            // and a PayPal donor spends two of its permits rather than one.
            var throttled = _throttleError(res);
            if (throttled) throw throttled;
            if (!res.ok) throw new Error('HTTP error ' + res.status);
            return res.json();
        })
        .then(function (orderData) {
            var errorDetail = orderData.details && orderData.details[0];

            // Recoverable: buyer's payment instrument was declined
            if (errorDetail && errorDetail.issue === 'INSTRUMENT_DECLINED') {
                return actions.restart();
            }

            if (!orderData.success) {
                throw new Error(orderData.error || _i18n.captureFailed);
            }

            _state.paymentMethod = paymentMethod;
            _state.paymentToken = {
                dataDescriptor: paymentMethod === 'VENMO'
                    ? 'COMMON.VENMO.INAPP.PAYMENT'
                    : 'COMMON.PAYPAL.INAPP.PAYMENT',
                dataValue: orderData.captureId || data.orderID
            };

            // If donation was already saved during capture, we can show success
            // directly. _submitDonation still fires for the thank-you redirect,
            // but the server's idempotency check prevents double-save.
            _submitDonation();
        })
        .catch(function (err) {
            console.error('[DONATION.Support] ' + paymentMethod + ' capture error:', err);
            _showError('paymentError', err.userMessage || _i18n.walletPaymentFailed.replace('{0}', paymentMethod));
            _state.isProcessing = false;
            _setButtonLoading(false);
        });
    }

    /**
     * AB#461: hides a wallet tab whose funding source the SDK will not render.
     *
     * A donor who picks a tab and gets an empty box has no way to tell whether the page is broken,
     * their connection is slow, or the option was never going to work. They leave.
     */
    function _hideWalletTab(tab, funding) {
        if (tab) tab.style.display = 'none';
        console.warn('[DONATION.Support] ' + funding + ' is not available in this browser; tab hidden');
    }

    /**
     * AB#461: whether the PayPal SDK will actually render this funding source in THIS browser.
     * Wrapped because constructing Buttons can throw for an unknown funding source, and an
     * exception while deciding which tabs to show must not take the whole payment section down.
     */
    function _walletEligible(funding) {
        try {
            return paypal.Buttons({ fundingSource: funding }).isEligible();
        } catch (e) {
            return false;
        }
    }

    /**
     * Renders (or re-renders) the PayPal SDK button into #paypal-button-target.
     * Called every time the PayPal tab is activated — fresh render each time
     * because PayPal SDK iframes lose interactivity after display:none toggling.
     */
    function _renderPayPalButton() {
        if (!window.paypal) return;
        var target = document.getElementById('paypal-button-target');
        if (!target) return;
        target.innerHTML = '';

        var buttons = paypal.Buttons({
            fundingSource: paypal.FUNDING.PAYPAL,
            style: { layout: 'vertical', color: 'black', shape: 'rect', label: 'donate', height: 52 },
            onClick: function (data, actions) {
                return _validateBeforeWalletPay() ? actions.resolve() : actions.reject();
            },
            createOrder: _paypalCreateOrder,
            onApprove: function (data, actions) {
                return _paypalOnApprove(data, actions, 'PAYPAL');
            },
            onError: function (err) {
                console.error('[DONATION.Support] PayPal error:', err);
                _showError('paymentError', _i18n.walletPaymentFailed.replace('{0}', 'PayPal'));
                _state.isProcessing = false;
                _setButtonLoading(false);
            }
        });

        // AB#461: see _renderVenmoButton. render() REJECTS for an ineligible funding source rather
        // than throwing, so an unhandled promise here is a silently empty box.
        if (!buttons.isEligible()) {
            _hideWalletTab(_elements.paypalTab, 'PayPal');
            return;
        }

        buttons.render('#paypal-button-target').catch(function () {
            _hideWalletTab(_elements.paypalTab, 'PayPal');
        });
    }

    /**
     * Renders (or re-renders) the Venmo SDK button into #venmo-button-target.
     * Called every time the Venmo tab is activated — fresh render each time.
     */
    function _renderVenmoButton() {
        if (!window.paypal) return;
        var target = document.getElementById('venmo-button-target');
        if (!target) return;
        target.innerHTML = '';

        // AB#461: ASK the SDK whether Venmo can render here, rather than assuming the config flag
        // settles it. Venmo has to hand off to the Venmo app and come back, so PayPal marks it
        // ineligible in most in-app webviews - Facebook and Instagram among them - and it is US and
        // mobile only. Fundraiser links are shared on exactly those apps, so this is not an edge
        // case: a donor taps the link in Facebook, picks Venmo and gets an empty box.
        //
        // The old code wrapped render() in try/catch, but an ineligible funding source makes
        // render() return a REJECTED PROMISE rather than throw. The catch never ran, so the very
        // fallback written to hide the tab was unreachable for the one case it existed for.
        var buttons;
        try {
            buttons = paypal.Buttons({
                fundingSource: paypal.FUNDING.VENMO,
                style: { color: 'black', shape: 'rect', label: 'donate', height: 52 },
                onClick: function (data, actions) {
                    return _validateBeforeWalletPay() ? actions.resolve() : actions.reject();
                },
                createOrder: _paypalCreateOrder,
                onApprove: function (data, actions) {
                    return _paypalOnApprove(data, actions, 'VENMO');
                },
                onError: function (err) {
                    console.error('[DONATION.Support] Venmo error:', err);
                    _showError('paymentError', _i18n.walletPaymentFailed.replace('{0}', 'Venmo'));
                    _state.isProcessing = false;
                    _setButtonLoading(false);
                }
            });
        } catch (e) {
            _hideWalletTab(_elements.venmoTab, 'Venmo');
            return;
        }

        if (!buttons.isEligible()) {
            _hideWalletTab(_elements.venmoTab, 'Venmo');
            return;
        }

        buttons.render('#venmo-button-target').catch(function () {
            _hideWalletTab(_elements.venmoTab, 'Venmo');
        });
    }

    /**
     * Switches active payment method tab and toggles card/wallet UI.
     */
    function _selectPaymentMethod(method) {
        _state.paymentMethod = method;

        // GA4: Track payment method selection
        _trackEvent('add_payment_info', { payment_type: method });

        // Update tab active state
        if (_elements.paymentMethodTabs) {
            _elements.paymentMethodTabs.querySelectorAll('.payment-tab').forEach(function (tab) {
                tab.classList.toggle('active', tab.dataset.method === method);
            });
        }

        var isCard = method === 'CARD';
        var isApplePay = method === 'APPLE_PAY';
        var isGooglePay = method === 'GOOGLE_PAY';
        var isPayPal = method === 'PAYPAL';
        var isVenmo = method === 'VENMO';

        // Toggle between card fields and SDK-rendered wallet buttons
        if (_elements.cardFieldsContainer) _elements.cardFieldsContainer.style.display = isCard ? '' : 'none';
        if (_elements.applePayButtonContainer) _elements.applePayButtonContainer.style.display = isApplePay ? '' : 'none';
        if (_elements.googlePayButtonContainer) _elements.googlePayButtonContainer.style.display = isGooglePay ? '' : 'none';
        if (_elements.paypalButtonContainer) _elements.paypalButtonContainer.style.display = isPayPal ? '' : 'none';
        if (_elements.venmoButtonContainer) _elements.venmoButtonContainer.style.display = isVenmo ? '' : 'none';

        // Re-render PayPal/Venmo SDK buttons fresh every time the tab is activated.
        // PayPal SDK iframes lose interactivity after display:none toggling,
        // so we destroy and recreate them on each tab switch.
        if (isPayPal) _renderPayPalButton();
        if (isVenmo) _renderVenmoButton();

        // Show "Donate Now" button only for card payments; wallets have their own buttons
        if (_elements.btnDonate) _elements.btnDonate.style.display = isCard ? '' : 'none';

        // Clear all validation errors when switching payment methods
        _clearAllErrors();
        _clearCardError('cardNumber');
        _clearCardError('cardExpiry');
        _clearCardError('cardCVV');
    }

    /**
     * Handles wallet pay button click. Initiates the wallet-specific payment flow.
     */
    function _handleWalletPay() {
        if (_state.isProcessing) return;

        // Validate non-payment fields first
        var amount = _getCurrentAmount();
        if (!amount || amount < _config.minimumDonation) {
            _showError('amountError', _i18n.selectDonationAmount + _config.minimumDonation + ').');
            _scrollToError(_elements.amountInput || qs('#donationAmount'));
            return;
        }

        if (!_validateForm()) return;

        _state.isProcessing = true;
        _setButtonLoading(true);

        // Route to wallet-specific flow (PayPal/Venmo handled by their own SDK buttons)
        switch (_state.paymentMethod) {
            case 'APPLE_PAY':
                _processApplePay(amount);
                break;
            case 'GOOGLE_PAY':
                _processGooglePay(amount);
                break;
            default:
                _showError('paymentError', _i18n.unsupportedPaymentMethod);
                _state.isProcessing = false;
                _setButtonLoading(false);
        }
    }

    /**
     * Apple Pay payment flow via ApplePaySession.
     */
    function _processApplePay(amount) {
        var coverFees = _elements.coverFees ? _elements.coverFees.checked : false;
        var totalAmount = coverFees ? amount + _calculateFee(amount) : amount;

        var request = {
            countryCode: 'US',
            currencyCode: 'USD',
            total: { label: 'Donation', amount: totalAmount.toFixed(2) },
            supportedNetworks: ['visa', 'masterCard', 'amex', 'discover'],
            merchantCapabilities: ['supports3DS']
        };

        var session = new ApplePaySession(3, request);

        session.onvalidatemerchant = function (event) {

            fetch('/Support/ValidateApplePayMerchant', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'RequestVerificationToken': AppUtils.getToken()
                },
                body: JSON.stringify({ validationUrl: event.validationURL })
            })
            .then(function (response) {
                if (!response.ok) throw new Error('Merchant validation failed (' + response.status + ')');
                return response.json();
            })
            .then(function (merchantSession) {
                session.completeMerchantValidation(merchantSession);
            })
            .catch(function (err) {
                console.error('[DONATION.Support] Apple Pay merchant validation error:', err);
                _showError('paymentError', _i18n.applePayUnavailable);
                _state.isProcessing = false;
                _setButtonLoading(false);
                session.abort();
            });
        };

        session.onpaymentauthorized = function (event) {
            var token = event.payment.token;
            _state.paymentToken = {
                dataDescriptor: 'COMMON.APPLE.INAPP.PAYMENT',
                dataValue: btoa(JSON.stringify(token.paymentData))
            };
            // Capture card details for thank-you display (e.g. "Apple Pay (Visa •••• 1234)")
            var pm = event.payment.token.paymentMethod;
            if (pm) {
                _state.paymentMethodDetail = {
                    cardNetwork: pm.network || '',
                    lastFour: pm.displayName || ''
                };
            }
            session.completePayment(ApplePaySession.STATUS_SUCCESS);
            _submitDonation();
        };

        session.oncancel = function () {
            _state.isProcessing = false;
            _setButtonLoading(false);
        };

        session.begin();
    }

    /**
     * Google Pay payment flow via Google Pay API.
     */
    function _processGooglePay(amount) {
        var coverFees = _elements.coverFees ? _elements.coverFees.checked : false;
        var totalAmount = coverFees ? amount + _calculateFee(amount) : amount;

        if (!_googlePayClient) {
            _showError('paymentError', _i18n.googlePayNotAvailable);
            _state.isProcessing = false;
            _setButtonLoading(false);
            return;
        }

        var paymentDataRequest = {
            apiVersion: 2,
            apiVersionMinor: 0,
            allowedPaymentMethods: [{
                type: 'CARD',
                parameters: {
                    allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
                    allowedCardNetworks: ['VISA', 'MASTERCARD', 'AMEX', 'DISCOVER']
                },
                tokenizationSpecification: {
                    type: 'PAYMENT_GATEWAY',
                    parameters: {
                        gateway: 'authorizenet',
                        gatewayMerchantId: _config.googlePayGatewayMerchantId || _config.acceptJsApiLoginId
                    }
                }
            }],
            merchantInfo: _config.googlePayMerchantId
                ? { merchantId: _config.googlePayMerchantId, merchantName: 'Rally4One' }
                : undefined,
            transactionInfo: {
                totalPriceStatus: 'FINAL',
                totalPrice: totalAmount.toFixed(2),
                currencyCode: 'USD',
                countryCode: 'US'
            }
        };

        _googlePayClient.loadPaymentData(paymentDataRequest)
            .then(function (paymentData) {
                var token = paymentData.paymentMethodData.tokenizationData.token;
                // DEBUG: Log token format and Google Pay config for troubleshooting
                _state.paymentToken = {
                    dataDescriptor: 'COMMON.GOOGLE.INAPP.PAYMENT',
                    dataValue: btoa(token)
                };
                // Capture card details for thank-you display (e.g. "Google Pay (Visa •••• 1234)")
                var info = paymentData.paymentMethodData.info;
                if (info) {
                    _state.paymentMethodDetail = {
                        cardNetwork: info.cardNetwork || '',
                        lastFour: info.cardDetails || ''
                    };
                }
                _submitDonation();
            })
            .catch(function (err) {
                if (err.statusCode !== 'CANCELED') {
                    console.error('[DONATION.Support] Google Pay error:', err);
                    _showError('paymentError', _i18n.walletPaymentFailed.replace('{0}', 'Google Pay'));
                }
                _state.isProcessing = false;
                _setButtonLoading(false);
            });
    }

    // PayPal/Venmo: handled by PayPal SDK buttons rendered in _initPayPalButtons().
    // No _processPayPal() needed — PayPal Buttons manage their own click → createOrder → capture flow.

    /**
     * Applies theme colors from data attributes on the donation wrapper element
     * via the shared window.Theme module.
     * @returns {void}
     */
    function _initializeTheme() {
        if (window.Theme && _elements.wrapper) {
            Theme.applyFromDataset(_elements.wrapper);
        }
    }

    /* =========================================================================
       AMOUNT HANDLING
       ========================================================================= */

    /**
     * Handles preset amount button clicks. Sets the clicked button as active,
     * updates the selected amount in state and the custom input field,
     * clears any amount error, and recalculates fees.
     * @param {Event} e - The click event from a preset amount button.
     * @returns {void}
     */
    function _handleAmountClick(e) {
        var btn = e.currentTarget;
        var amount = parseFloat(btn.dataset.amount);

        _elements.amountButtons.forEach(function (b) {
            b.classList.remove('active');
        });
        btn.classList.add('active');

        _state.selectedAmount = amount;
        if (_elements.amountInput) {
            _elements.amountInput.value = amount;
        }

        _clearError('amountError');
        _updateFeeCalculation();
        _checkAverageNudge();
    }

    /**
     * Handles custom amount input changes. Deselects any active preset button,
     * parses the input value, validates against the minimum donation amount,
     * and recalculates fees.
     * @returns {void}
     */
    function _handleAmountInput() {
        _elements.amountButtons.forEach(function (btn) {
            btn.classList.remove('active');
        });

        var value = parseFloat(_elements.amountInput.value);
        _state.selectedAmount = isNaN(value) ? 0 : value;

        // Live minimum amount validation
        if (_elements.amountInput.value.trim() !== '' && _state.selectedAmount > 0 && _state.selectedAmount < _config.minimumDonation) {
            _showError('amountError', _i18n.minimumDonation + _config.minimumDonation);
        } else {
            _clearError('amountError');
        }

        _updateFeeCalculation();
        _checkAverageNudge();
    }

    /**
     * Shows a gentle nudge if the selected amount is below the platform average.
     * Encourages the donor to round up to the average without being pushy.
     */
    function _checkAverageNudge() {
        var nudgeEl = qs('#averageNudge');
        if (!nudgeEl) return;

        var avg = parseFloat(nudgeEl.dataset.platformAverage) || 0;
        if (avg <= 0) return;

        var amount = _state.selectedAmount || 0;
        if (amount <= 0 || amount >= avg) {
            nudgeEl.style.display = 'none';
            return;
        }

        var threshold = avg * 0.5;
        var target, message;

        if (amount >= threshold) {
            target = avg;
            var diff = Math.ceil(avg - amount);

            // AB#285: "campaigns", never "sports campaigns" or "school campaigns". avg is the higher
            // of the two segment averages (AB#267) and naming the segment was how that stayed
            // literally true - but it tells a donor which bucket their cause sits in and invites the
            // comparison. The number is the nudge; the segmentation is ours, not theirs.
            message = 'Thank you! Just $' + diff + ' more to match the $' + avg + ' average for campaigns. Every bit helps!';
        } else {
            target = Math.ceil(amount / 5) * 5 + 5;
            message = 'Thank you! Want to round up to $' + target + ' for even more impact?';
        }

        var nudgeText = qs('#nudgeText');
        if (nudgeText) nudgeText.textContent = message;

        var nudgeBtn = qs('#nudgeUpBtn');
        if (nudgeBtn) nudgeBtn.textContent = 'Round up to $' + target;

        nudgeEl.style.display = '';

        if (nudgeBtn) {
            nudgeBtn.onclick = function () {
                var originalAmount = amount;
                _state.selectedAmount = target;
                if (_elements.amountInput) _elements.amountInput.value = target;
                _elements.amountButtons.forEach(function (b) { b.classList.remove('active'); });
                _clearError('amountError');
                _updateFeeCalculation();
                nudgeEl.style.display = 'none';

                var nudgeField = qs('#usedRoundUpNudge');
                if (nudgeField) nudgeField.value = 'true';
                var origField = qs('#originalAmountBeforeNudge');
                if (origField) origField.value = originalAmount;

                if (typeof gtag === 'function') {
                    gtag('event', 'round_up_nudge', {
                        event_category: 'donation',
                        original_amount: originalAmount,
                        nudge_amount: target,
                        uplift: target - originalAmount
                    });
                }
            };
        }
    }

    /**
     * Returns the current donation amount from state or the custom input field.
     * Falls back to the input field value if state has no selection. Returns 0
     * if no valid amount is available.
     * @returns {number} The current donation amount in dollars.
     */
    function _getCurrentAmount() {
        if (_state.selectedAmount !== null && !isNaN(_state.selectedAmount)) {
            return _state.selectedAmount;
        }
        if (_elements.amountInput) {
            var parsed = parseFloat(_elements.amountInput.value);
            return isNaN(parsed) ? 0 : parsed;
        }
        return 0;
    }

    /* =========================================================================
       FEE CALCULATION
       ========================================================================= */

    /**
     * Calculates the processing fee for a given donation amount.
     * Uses the configured fee percentage and rounds to 2 decimal places.
     * @param {number} amount - The donation amount in dollars.
     * @returns {number} The calculated processing fee in dollars.
     */
    function _calculateFee(amount) {
        return Math.round(amount * _config.feePercentage * 100) / 100;
    }

    /**
     * Detects card network from the card number prefix.
     * @param {string} num - Raw card number (digits only).
     * @returns {string} Network name (Visa, Mastercard, Amex, Discover) or 'Card'.
     */
    function _detectCardNetwork(num) {
        if (/^4/.test(num)) return 'Visa';
        if (/^5[1-5]/.test(num) || /^2[2-7]/.test(num)) return 'Mastercard';
        if (/^3[47]/.test(num)) return 'Amex';
        if (/^6(?:011|5)/.test(num)) return 'Discover';
        return 'Card';
    }

    /**
     * Updates the fee breakdown display based on the current donation amount
     * and the cover-fees checkbox state. Populates the fee description percentage,
     * donation amount, fee amount, total amount, and toggles breakdown visibility.
     * @returns {void}
     */
    function _updateFeeCalculation() {
        var amount = _getCurrentAmount();
        var coverFees = _elements.coverFees && _elements.coverFees.checked;

        if (_elements.feeDescription) {
            _elements.feeDescription.textContent = (_config.feePercentage * 100).toFixed(1) + '%';
        }

        var fee = coverFees ? _calculateFee(amount) : 0;
        var total = amount + fee;

        if (_elements.feeDisplayDonation) {
            _elements.feeDisplayDonation.textContent = '$' + amount.toFixed(2);
        }
        if (_elements.feeDisplayFee) {
            _elements.feeDisplayFee.textContent = '$' + fee.toFixed(2);
        }
        if (_elements.feeDisplayTotal) {
            _elements.feeDisplayTotal.textContent = '$' + total.toFixed(2);
        }
        if (_elements.feeBreakdown) {
            _elements.feeBreakdown.style.display = coverFees ? 'block' : 'none';
        }
    }

    /**
     * Shows the fee breakdown section if the cover-fees checkbox is already
     * checked on page load (e.g. from browser autofill or server-rendered state).
     * @returns {void}
     */
    function _showFeeBreakdownIfChecked() {
        if (_elements.coverFees && _elements.coverFees.checked && _elements.feeBreakdown) {
            _elements.feeBreakdown.style.display = 'block';
        }
    }

    /* =========================================================================
       CARD FORMATTING
       ========================================================================= */

    /**
     * Formats card number input with spaces every 4 digits.
     * Strips all non-digit characters, then inserts spaces between groups of four.
     * @param {Event} e - The input event from the card number field.
     * @returns {void}
     */
    function _formatCardNumber(e) {
        var value = e.target.value.replace(/\s/g, '').replace(/\D/g, '');
        var formatted = value.match(/.{1,4}/g);
        e.target.value = formatted ? formatted.join(' ') : value;
    }

    /**
     * Formats expiry input as MM/YY. Strips non-digit characters and
     * inserts a forward slash after the first two digits.
     * @param {Event} e - The input event from the card expiry field.
     * @returns {void}
     */
    function _formatCardExpiry(e) {
        var value = e.target.value.replace(/\D/g, '');
        if (value.length >= 2) {
            value = value.slice(0, 2) + '/' + value.slice(2, 4);
        }
        e.target.value = value;
    }

    /**
     * Strips non-digit characters from the CVV input field.
     * @param {Event} e - The input event from the card CVV field.
     * @returns {void}
     */
    function _formatCardCVV(e) {
        e.target.value = e.target.value.replace(/\D/g, '');
    }

    /**
     * Formats billing ZIP to alphanumeric characters, dashes, and spaces, uppercased.
     * Supports international postal code formats (US, Canada, UK, etc.).
     * @param {Event} e - The input event from the billing ZIP field.
     * @returns {void}
     */
    function _formatBillingZip(e) {
        // Allow alphanumeric for international postal codes, but strip special chars
        // US: 12345 or 12345-6789
        // Canada: A1B 2C3
        // UK: SW1A 1AA
        e.target.value = e.target.value.replace(/[^a-zA-Z0-9\s\-]/g, '').toUpperCase();
    }

    /* =========================================================================
       VALIDATION
       ========================================================================= */

    /**
     * Validates the donation form fields: amount (must meet minimum), first name,
     * last name, and email (using AppUtils.isValidEmail). Shows inline error
     * messages for any invalid fields.
     * @returns {boolean} True if all form fields are valid, false otherwise.
     */
    function _validateForm() {
        var isValid = true;
        var firstErrorField = null;
        _clearAllErrors();

        var amount = _getCurrentAmount();
        if (!amount || amount < _config.minimumDonation) {
            _showError('amountError', _i18n.minimumDonation + _config.minimumDonation);
            if (!firstErrorField) firstErrorField = _elements.amountInput || qs('#donationAmount');
            isValid = false;
        }

        if (!_elements.firstName || !_elements.firstName.value.trim()) {
            _showError('firstNameError', _i18n.firstNameRequired);
            if (!firstErrorField) firstErrorField = _elements.firstName;
            isValid = false;
        }

        if (!_elements.lastName || !_elements.lastName.value.trim()) {
            _showError('lastNameError', _i18n.lastNameRequired);
            if (!firstErrorField) firstErrorField = _elements.lastName;
            isValid = false;
        }

        var email = _elements.email ? _elements.email.value.trim() : '';
        if (!email || !AppUtils.isValidEmail(email)) {
            _showError('emailError', _i18n.invalidEmail);
            if (!firstErrorField) firstErrorField = _elements.email;
            isValid = false;
        }

        if (!isValid && firstErrorField) {
            _scrollToError(firstErrorField);
        }

        return isValid;
    }

    /**
     * Smooth-scrolls to the first invalid field and focuses it.
     * Uses a small offset so the field isn't pinned to the very top of the viewport.
     * @param {HTMLElement} field - The input element to scroll to.
     * @returns {void}
     */
    function _scrollToError(field) {
        if (!field) return;
        var offset = 100; // px from top of viewport
        var top = field.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top: top, behavior: 'smooth' });
        // Focus after scroll completes so the field is visible
        setTimeout(function () { if (field.focus) field.focus(); }, 400);
    }

    /**
     * Validates card payment fields: card number (13-19 digits), expiry (MM/YY format
     * with valid month 1-12), and CVV (3-4 digits). Shows per-field error states
     * on invalid inputs and clears them on valid ones.
     * @returns {boolean} True if all card fields are valid, false otherwise.
     */
    function _validateCardFields() {
        var isValid = true;
        var firstErrorField = null;

        var cardNumber = _elements.cardNumber ? _elements.cardNumber.value.replace(/\s/g, '') : '';
        if (!cardNumber || cardNumber.length < 13 || cardNumber.length > 19) {
            _showCardError('cardNumber', _i18n.invalidCard);
            if (!firstErrorField) firstErrorField = _elements.cardNumber;
            isValid = false;
        } else {
            _clearCardError('cardNumber');
        }

        var expiry = _elements.cardExpiry ? _elements.cardExpiry.value : '';
        if (!expiry || !/^\d{2}\/\d{2}$/.test(expiry)) {
            _showCardError('cardExpiry', _i18n.invalidExpiry);
            if (!firstErrorField) firstErrorField = _elements.cardExpiry;
            isValid = false;
        } else {
            var month = parseInt(expiry.split('/')[0], 10);
            if (month < 1 || month > 12) {
                _showCardError('cardExpiry', _i18n.invalidMonth);
                if (!firstErrorField) firstErrorField = _elements.cardExpiry;
                isValid = false;
            } else {
                _clearCardError('cardExpiry');
            }
        }

        var cvv = _elements.cardCVV ? _elements.cardCVV.value : '';
        if (!cvv || cvv.length < 3 || cvv.length > 4) {
            _showCardError('cardCVV', _i18n.invalidCVV);
            if (!firstErrorField) firstErrorField = _elements.cardCVV;
            isValid = false;
        } else {
            _clearCardError('cardCVV');
        }

        // TEMPORARILY DISABLED: ZIP validation for testing
        // var billingZip = _elements.billingZip ? _elements.billingZip.value.trim() : '';
        // if (!billingZip || billingZip.length < 3) {
        //     _showCardError('billingZip', 'Please enter billing ZIP code');
        //     isValid = false;
        // } else {
        //     _clearCardError('billingZip');
        // }

        if (!isValid && firstErrorField) {
            _scrollToError(firstErrorField);
        }

        return isValid;
    }

    /* =========================================================================
       ERROR HANDLING
       ========================================================================= */

    /**
     * Shows an error message element by its cached key or DOM id.
     * Sets the element text content and makes it visible.
     * @param {string} elementId - The _elements key or DOM id of the error element.
     * @param {string} message - The error message to display.
     * @returns {void}
     */
    function _showError(elementId, message) {
        var el = _elements[elementId] || qs('#' + elementId);
        if (el) {
            el.textContent = message;
            el.style.display = 'block';
        }
    }

    /**
     * Clears an error message element by its cached key or DOM id.
     * Empties the text content and hides the element.
     * @param {string} elementId - The _elements key or DOM id of the error element.
     * @returns {void}
     */
    function _clearError(elementId) {
        var el = _elements[elementId] || qs('#' + elementId);
        if (el) {
            el.textContent = '';
            el.style.display = 'none';
        }
    }

    /**
     * Clears all form error messages (amount, first name, last name, email, payment).
     * @returns {void}
     */
    function _clearAllErrors() {
        ['amountError', 'firstNameError', 'lastNameError', 'emailError', 'paymentError'].forEach(_clearError);
    }

    /**
     * Shows error state on a card input field. Adds the error CSS class to the
     * input and displays the associated error message element.
     * @param {string} fieldId - The DOM id of the card input field.
     * @param {string} message - The error message to display below the field.
     * @returns {void}
     */
    function _showCardError(fieldId, message) {
        var input = qs('#' + fieldId);
        var errorEl = qs('#' + fieldId + 'Error');
        if (input) input.classList.add('error');
        if (errorEl) {
            errorEl.textContent = message;
            errorEl.style.display = 'block';
        }
    }

    /**
     * Clears error state on a card input field. Removes the error CSS class
     * from the input and hides the associated error message element.
     * @param {string} fieldId - The DOM id of the card input field.
     * @returns {void}
     */
    function _clearCardError(fieldId) {
        var input = qs('#' + fieldId);
        var errorEl = qs('#' + fieldId + 'Error');
        if (input) input.classList.remove('error');
        if (errorEl) {
            errorEl.textContent = '';
            errorEl.style.display = 'none';
        }
    }

    /* =========================================================================
       FORM SUBMISSION
       ========================================================================= */

    /**
     * Handles form submit event. Prevents default submission, guards against
     * double-submit, validates form and card fields, then initiates payment
     * tokenization via Accept.js.
     * @param {Event} e - The form submit event.
     * @returns {void}
     */
    function _handleSubmit(e) {
        e.preventDefault();

        if (_state.isProcessing) return;

        // For wallet payments, the wallet button handles submission — not the form
        if (_state.paymentMethod !== 'CARD') {
            _handleWalletPay();
            return;
        }

        if (!_validateForm() || !_validateCardFields()) {
            return;
        }

        _state.isProcessing = true;
        _setButtonLoading(true);

        // Get payment token from Authorize.Net Accept.js
        _getPaymentToken();
    }

    /**
     * Calls Authorize.Net Accept.js to tokenize the card data into an opaque
     * payment nonce. Builds the secureData payload from card inputs and config
     * credentials, then dispatches to Accept.js with _handlePaymentResponse
     * as the callback. Shows an error if Accept.js is not yet loaded.
     * @returns {void}
     */
    function _getPaymentToken() {
        // Check if Accept.js is loaded
        if (typeof Accept === 'undefined') {
            _showError('paymentError', _i18n.paymentSystemLoading);
            _state.isProcessing = false;
            _setButtonLoading(false);
            return;
        }

        var expiryParts = _elements.cardExpiry.value.split('/');
        var expMonth = expiryParts[0] || '';
        var expYear = expiryParts[1] ? '20' + expiryParts[1] : '';

        var secureData = {
            authData: {
                clientKey: _config.acceptJsClientKey,
                apiLoginID: _config.acceptJsApiLoginId
            },
            cardData: {
                cardNumber: _elements.cardNumber.value.replace(/\s/g, ''),
                month: expMonth,
                year: expYear,
                cardCode: _elements.cardCVV.value,
                // Note: Accept.js does NOT accept ZIP in cardData
                // ZIP is sent separately in the server-side transaction request
            }
        };

        // Call Accept.js to get payment nonce
        Accept.dispatchData(secureData, _handlePaymentResponse);
    }

    /**
     * Callback from Authorize.Net Accept.js after tokenization attempt.
     * On success, stores the opaque payment token in _state and proceeds to
     * _submitDonation(). On error, displays the error messages from Accept.js,
     * resets processing state, and shows a toast notification.
     * @param {Object} response - The Accept.js response object.
     * @param {Object} response.messages - Contains resultCode and message array.
     * @param {string} response.messages.resultCode - Ok on success, Error on failure.
     * @param {Array<{code: string, text: string}>} response.messages.message - Error messages.
     * @param {Object} [response.opaqueData] - Present on success; contains the payment token.
     * @param {string} [response.opaqueData.dataDescriptor] - Token type descriptor.
     * @param {string} [response.opaqueData.dataValue] - The opaque payment nonce value.
     * @returns {void}
     */
    function _handlePaymentResponse(response) {
        if (response.messages.resultCode === 'Error') {
            var errors = response.messages.message || [];
            
            // Log detailed error info for debugging
            console.error('[DONATION.Support] Accept.js Error:', {
                resultCode: response.messages.resultCode,
                errors: errors.map(function(m) { 
                    return { code: m.code, text: m.text }; 
                })
            });
            
            var errorMsg = errors.map(function (m) {
                return m.text;
            }).join(', ');

            _showError('paymentError', errorMsg || _i18n.paymentValidationFailed);
            _state.isProcessing = false;
            _setButtonLoading(false);

            if (window.Toast) {
                Toast.error('Payment failed: ' + (errorMsg || 'Please check your card details.'));
            }
            return;
        }

        // Store the opaque data from Accept.js
        _state.paymentToken = {
            dataDescriptor: response.opaqueData.dataDescriptor,
            dataValue: response.opaqueData.dataValue
        };

        // Capture card details for thank-you display (e.g. "Credit Card (Visa •••• 1234)")
        var rawNum = _elements.cardNumber ? _elements.cardNumber.value.replace(/\s/g, '') : '';
        if (rawNum.length >= 4) {
            _state.paymentMethodDetail = {
                cardNetwork: _detectCardNetwork(rawNum),
                lastFour: rawNum.slice(-4)
            };
        }

        _submitDonation();
    }

    /**
     * POSTs the donation data with the Accept.js payment token to the server.
     * Builds the full payload including donor info, amounts, fee coverage,
     * tracking data, and the opaque payment nonce. On success, shows the
     * success modal. On failure, displays the error. Resets processing state
     * in the finally block.
     * @returns {void}
     */
    function _submitDonation() {
        _setProcessingOverlay(true);
        var amount = _getCurrentAmount();
        var coverFees = _elements.coverFees ? _elements.coverFees.checked : false;
        var processingFee = coverFees ? _calculateFee(amount) : 0;

        // GA4: Track checkout attempt (items enables ecommerce funnel)
        _trackEvent('begin_checkout', {
            currency: 'USD',
            value: amount,
            payment_type: _state.paymentMethod,
            cover_fees: coverFees,
            items: [{
                item_id: 'donation_' + _config.eventId,
                item_name: _config.eventName || 'Donation',
                item_category: 'Donation',
                price: amount,
                quantity: 1
            }]
        });

        var data = {
            eventId: parseInt(_config.eventId, 10),
            advocateId: parseInt(_config.advocateId, 10),
            contactId: _config.contactId ? parseInt(_config.contactId, 10) : null,
            donationAmount: amount,
            totalAmount: amount + processingFee,
            coverFees: coverFees,
            processingFee: processingFee,
            firstName: _elements.firstName.value.trim(),
            lastName: _elements.lastName.value.trim(),
            email: _elements.email.value.trim(),
            phone: _elements.phone ? _elements.phone.value.trim() : null,
            message: _elements.message ? _elements.message.value.trim() : null,
            paymentMethod: _state.paymentMethod,
            trackingCode: _config.trackingCode || null,
            sourceChannel: _config.sourceChannel || 'direct',
            rawSourceChannel: _config.rawSourceChannel || null,
            opaqueDataDescriptor: _state.paymentToken.dataDescriptor,
            opaqueDataValue: _state.paymentToken.dataValue,
            // Add billing ZIP for AVS verification (helps with AMEX/Discover)
            billingZip: _elements.billingZip ? _elements.billingZip.value.trim() : null,
            usedRoundUpNudge: qs('#usedRoundUpNudge') ? qs('#usedRoundUpNudge').value === 'true' : false,
            originalAmountBeforeNudge: qs('#originalAmountBeforeNudge') ? parseFloat(qs('#originalAmountBeforeNudge').value) || 0 : 0
        };

        fetch(_config.submitUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'RequestVerificationToken': AppUtils.getToken()
            },
            body: JSON.stringify(data)
        })
            .then(function (response) {
                // AB#425: 429 carries a Retry-After the donor can act on; everything else stays
                // a generic failure, because gateway text must not reach the donor.
                var throttled = _throttleError(response);
                if (throttled) throw throttled;
                if (!response.ok) throw new Error('HTTP error ' + response.status);
                return response.json();
            })
            .then(function (result) {
                if (result.success) {
                    // GA4: Track successful donation (purchase event with items for ecommerce reports)
                    _trackEvent('purchase', {
                        transaction_id: result.transactionId || '',
                        currency: 'USD',
                        value: amount + processingFee,
                        donation_amount: amount,
                        processing_fee: processingFee,
                        payment_type: _state.paymentMethod,
                        cover_fees: coverFees,
                        items: [{
                            item_id: 'donation_' + _config.eventId,
                            item_name: _config.eventName || 'Donation',
                            item_category: 'Donation',
                            price: amount,
                            quantity: 1
                        }]
                    });
                    _showSuccess(result);
                } else {
                    // Log full server response for debugging payment failures
                    console.error('[DONATION.Support] Payment failed - full server response:', JSON.stringify(result, null, 2));
                    // GA4: Track payment failure
                    _trackEvent('payment_error', {
                        payment_type: _state.paymentMethod,
                        error_message: result.error || _i18n.paymentFailed,
                        value: amount
                    });
                    _showError('paymentError', result.error || _i18n.paymentFailed);
                }
            })
            .catch(function (error) {
                console.error('[DONATION.Support] Submit error:', error);
                // GA4: Track network/server error
                _trackEvent('payment_error', {
                    payment_type: _state.paymentMethod,
                    error_message: error.message || _i18n.networkError,
                    value: amount
                });
                _showError('paymentError', error.userMessage || _i18n.errorTryAgain);
            })
            .finally(function () {
                _state.isProcessing = false;
                _state.paymentToken = null;
                _setButtonLoading(false);
                _setProcessingOverlay(false);
            });
    }

    /* =========================================================================
       UI HELPERS
       ========================================================================= */

    /**
     * Toggles the submit button disabled state and loading text.
     * When loading, disables the button and shows Processing text.
     * When not loading, enables the button and shows Donate Now text.
     * @param {boolean} isLoading - True to show loading state, false to restore default.
     * @returns {void}
     */
    function _setButtonLoading(isLoading) {
        if (!_elements.submitButton) return;

        _elements.submitButton.disabled = isLoading;
        var span = _elements.submitButton.querySelector('span');
        if (span) {
            span.textContent = isLoading ? _i18n.processing : _i18n.donateNow;
        }
    }

    /**
     * Shows or hides a full-page processing overlay with blur backdrop.
     * Creates the overlay element on first use and reuses it thereafter.
     * @param {boolean} show - True to show the overlay, false to hide it.
     */
    function _setProcessingOverlay(show) {
        var overlay = document.getElementById('donationProcessingOverlay');
        if (!overlay) {
            overlay = document.createElement('div');
            overlay.id = 'donationProcessingOverlay';
            overlay.className = 'donation-processing-overlay';
            overlay.innerHTML =
                '<div class="processing-spinner-container">' +
                    '<div class="processing-spinner"></div>' +
                    '<p class="processing-text">' + _i18n.processingYourDonation + '</p>' +
                '</div>';
            document.body.appendChild(overlay);
        }
        overlay.classList.toggle('active', show);
    }

    /**
     * Smooth-scrolls the viewport to the donation form container element.
     * @returns {void}
     */
    function _scrollToForm() {
        var formContainer = qs('.donation-form-container');
        if (formContainer) {
            formContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }

    /**
     * Animates the fundraising progress bar width on page load.
     * Reads the target percentage from the data-percent attribute and
     * transitions the bar width after a 300ms delay.
     * @returns {void}
     */
    function _animateProgressBar() {
        setTimeout(function () {
            if (_elements.progressFill) {
                var percent = parseFloat(_elements.progressFill.dataset.percent || '0');
                _elements.progressFill.style.width = Math.min(100, percent) + '%';
            }
        }, 300);
    }

    /**
     * Sets up an IntersectionObserver for fade-in animations.
     * Observes all elements with the fade-in class and adds the visible
     * class when they enter the viewport.
     * @returns {void}
     */
    function _initializeFadeIn() {
        var observer = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
                if (entry.isIntersecting) {
                    entry.target.classList.add('visible');
                }
            });
        });

        qsa('.fade-in').forEach(function (el) {
            observer.observe(el);
        });
    }

    /* =========================================================================
       SUCCESS MODAL
       ========================================================================= */

    /**
     * Populates and shows the success modal with donation details after a
     * successful payment. Displays the donation amount, processing fee (if covered),
     * total charged, and transaction ID. Shows a toast notification.
     * @param {Object} data - The server response from the donation submission.
     * @param {string} [data.transactionId] - The payment gateway transaction ID.
     * @returns {void}
     */
    function _showSuccess(data) {
        var amount = _getCurrentAmount();
        var coverFees = _elements.coverFees ? _elements.coverFees.checked : false;
        var fee = coverFees ? _calculateFee(amount) : 0;
        var total = amount + fee;

        if (_elements.successAmount) {
            _elements.successAmount.textContent = '$' + amount.toFixed(2);
        }
        if (_elements.successPaymentMethod) {
            var methodNames = {
                'CARD': _i18n.creditCard,
                'APPLE_PAY': 'Apple Pay',
                'GOOGLE_PAY': 'Google Pay',
                'PAYPAL': 'PayPal',
                'VENMO': 'Venmo'
            };
            var label = methodNames[_state.paymentMethod] || _state.paymentMethod;
            var detail = _state.paymentMethodDetail;
            if (detail && detail.lastFour) {
                var network = detail.cardNetwork || '';
                // Title-case the network name (Google Pay returns "VISA", Apple returns "visa")
                if (network) network = network.charAt(0).toUpperCase() + network.slice(1).toLowerCase();
                label += ' (' + (network ? network + ' \u2022\u2022\u2022\u2022 ' : '\u2022\u2022\u2022\u2022 ') + detail.lastFour + ')';
            }
            _elements.successPaymentMethod.textContent = label;
        }
        if (_elements.processingFeeRow) {
            _elements.processingFeeRow.style.display = fee > 0 ? 'flex' : 'none';
        }
        if (_elements.successProcessingFee) {
            _elements.successProcessingFee.textContent = '$' + fee.toFixed(2);
        }
        if (_elements.successTotalCharged) {
            _elements.successTotalCharged.textContent = '$' + total.toFixed(2);
        }
        if (_elements.successTransaction) {
            _elements.successTransaction.textContent = data.transactionId || '';
        }
        if (_elements.successModal) {
            _elements.successModal.style.display = 'flex';
        }

        // Wire up share buttons
        _initShareButtons();

        if (window.Toast) {
            Toast.success('Thank you for your donation!');
        }
    }

    /**
     * Initializes share button click handlers in the success modal.
     * Reads share data from the .share-buttons container's data attributes.
     * @returns {void}
     */
    function _initShareButtons() {
        var container = document.querySelector('.share-buttons');
        if (!container) return;

        var shareUrl = encodeURIComponent(container.dataset.shareUrl || '');
        var shareTitle = encodeURIComponent(container.dataset.shareTitle || '');
        var shareText = encodeURIComponent(container.dataset.shareText || '');

        var facebook = document.getElementById('shareFacebook');
        var twitter = document.getElementById('shareTwitter');
        var linkedin = document.getElementById('shareLinkedIn');
        var copyLink = document.getElementById('shareCopyLink');

        if (facebook) {
            facebook.onclick = function () {
                window.open('https://www.facebook.com/sharer/sharer.php?u=' + shareUrl,
                    'facebook-share', 'width=580,height=400,menubar=no,toolbar=no');
            };
        }

        if (twitter) {
            twitter.onclick = function () {
                window.open('https://twitter.com/intent/tweet?url=' + shareUrl + '&text=' + shareText,
                    'twitter-share', 'width=580,height=400,menubar=no,toolbar=no');
            };
        }

        if (linkedin) {
            linkedin.onclick = function () {
                window.open('https://www.linkedin.com/sharing/share-offsite/?url=' + shareUrl,
                    'linkedin-share', 'width=580,height=400,menubar=no,toolbar=no');
            };
        }

        if (copyLink) {
            copyLink.onclick = function () {
                var url = container.dataset.shareUrl || '';
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    navigator.clipboard.writeText(url).then(function () {
                        if (window.Toast) Toast.success('Link copied to clipboard!');
                    });
                } else {
                    // Fallback for older browsers
                    var input = document.createElement('input');
                    input.value = url;
                    document.body.appendChild(input);
                    input.select();
                    document.execCommand('copy');
                    document.body.removeChild(input);
                    if (window.Toast) Toast.success('Link copied to clipboard!');
                }
            };
        }

        // Native Web Share API (mobile)
        if (navigator.share) {
            var nativeBtn = document.createElement('button');
            nativeBtn.type = 'button';
            nativeBtn.className = 'share-btn share-btn--native';
            nativeBtn.title = 'More sharing options';
            nativeBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
                '<circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>' +
                '<line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>';
            nativeBtn.onclick = function () {
                navigator.share({
                    title: decodeURIComponent(shareTitle),
                    text: decodeURIComponent(shareText),
                    url: decodeURIComponent(shareUrl)
                }).catch(function () { /* user cancelled */ });
            };
            container.appendChild(nativeBtn);
        }
    }

    /**
     * Hides the success modal and resets the entire form to its initial state.
     * Exposed as a public method for external close triggers.
     * @returns {void}
     */
    function closeSuccessModal() {
        if (_elements.successModal) {
            _elements.successModal.style.display = 'none';
        }
        _resetForm();
    }

    /**
     * Resets the entire form state to defaults. Calls form.reset(), clears the
     * selected amount, deactivates all preset buttons, unchecks cover-fees,
     * hides the fee breakdown, clears all errors, and resets processing state.
     * @returns {void}
     */
    function _resetForm() {
        if (_elements.form) {
            _elements.form.reset();
        }

        _state.selectedAmount = null;
        _elements.amountButtons.forEach(function (btn) {
            btn.classList.remove('active');
        });

        if (_elements.amountInput) {
            _elements.amountInput.value = '';
        }
        if (_elements.coverFees) {
            _elements.coverFees.checked = false;
        }
        if (_elements.feeBreakdown) {
            _elements.feeBreakdown.style.display = 'none';
        }

        _clearAllErrors();
        _updateFeeCalculation();

        _state.isProcessing = false;
        _state.paymentToken = null;
        _state.paymentMethodDetail = null;
        _setButtonLoading(false);
    }

    /* =========================================================================
       PUBLIC API
       ========================================================================= */

    return {
        init: init,
        closeSuccessModal: closeSuccessModal,

        // Expose for Accept.js callback
        handlePaymentResponse: _handlePaymentResponse
    };

})();
